import { Card } from '../types';
import { isOnePieceGame } from '../constants/games';

export interface DeckItem {
  card: Card;
  quantity: number;
  section: string;
}

export function getCardCode(card: Card): string {
  if (card.code && typeof card.code === 'string') return card.code.trim();
  if (card.number && typeof card.number === 'string') return card.number.trim();
  if (typeof card.id === 'string' && /^[A-Za-z0-9-]+$/i.test(card.id) && !card.id.includes('__')) {
    return card.id.trim();
  }
  return '';
}

export function getOnePieceCategory(item: DeckItem): string {
  const sec = (item.section || '').trim();
  if (/leader/i.test(sec)) return 'Leader';
  if (/character/i.test(sec)) return 'Character';
  if (/event/i.test(sec)) return 'Event';
  if (/stage/i.test(sec)) return 'Stage';

  const type = (item.card.type || item.card.cardType || item.card.supertype || '').trim();
  if (/leader/i.test(type)) return 'Leader';
  if (/event/i.test(type)) return 'Event';
  if (/stage/i.test(type)) return 'Stage';
  if (/character/i.test(type)) return 'Character';

  return sec || 'Character';
}

export function generateOnePieceDeckText(deckItems: DeckItem[]): string {
  const groups: Record<string, { card: Card; quantity: number }[]> = {
    'Leader': [],
    'Character': [],
    'Event': [],
    'Stage': []
  };

  deckItems.forEach(item => {
    const cat = getOnePieceCategory(item);
    if (!groups[cat]) {
      groups[cat] = [];
    }
    const existing = groups[cat].find(g => g.card.id === item.card.id || (getCardCode(g.card) && getCardCode(g.card) === getCardCode(item.card) && g.card.name === item.card.name));
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      groups[cat].push({ card: item.card, quantity: item.quantity });
    }
  });

  const categoriesOrder = ['Leader', 'Character', 'Event', 'Stage', ...Object.keys(groups).filter(k => !['Leader', 'Character', 'Event', 'Stage'].includes(k))];

  const sectionsText: string[] = [];

  categoriesOrder.forEach(cat => {
    const list = groups[cat];
    if (!list || list.length === 0) return;

    const totalQty = list.reduce((sum, i) => sum + i.quantity, 0);

    let header = cat;
    if (cat.toLowerCase() !== 'leader') {
      header = `${cat} (${totalQty})`;
    }

    const lines = list.map(i => {
      const code = getCardCode(i.card);
      const codePart = code ? ` (${code})` : '';
      return `${i.quantity} ${i.card.name}${codePart}`;
    });

    sectionsText.push(`${header}\n${lines.join('\n')}`);
  });

  return sectionsText.join('\n\n');
}

export function generateGenericDeckText(deckItems: DeckItem[], deckName?: string): string {
  const groups: Record<string, { card: Card; quantity: number }[]> = {};

  deckItems.forEach(item => {
    const sec = item.section || 'Main';
    if (!groups[sec]) groups[sec] = [];
    const existing = groups[sec].find(g => g.card.id === item.card.id);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      groups[sec].push({ card: item.card, quantity: item.quantity });
    }
  });

  const sectionsText: string[] = [];

  Object.entries(groups).forEach(([sec, list]) => {
    if (list.length === 0) return;
    const totalQty = list.reduce((sum, i) => sum + i.quantity, 0);
    const lines = list.map(i => {
      const code = getCardCode(i.card);
      const codePart = code ? ` (${code})` : '';
      return `${i.quantity} ${i.card.name}${codePart}`;
    });
    sectionsText.push(`${sec} (${totalQty})\n${lines.join('\n')}`);
  });

  return sectionsText.join('\n\n');
}

export function exportDeckToText(gameName: string | number | any, deckItems: DeckItem[]): string {
  if (isOnePieceGame(gameName)) {
    return generateOnePieceDeckText(deckItems);
  }
  return generateGenericDeckText(deckItems);
}

export interface ParsedDeckLine {
  name: string;
  quantity: number;
  code?: string;
  section: string;
}

export function parseDeckListText(text: string, defaultSection: string = 'Main'): ParsedDeckLine[] {
  const lines = text.split('\n');
  let currentSection = defaultSection;
  const results: ParsedDeckLine[] = [];

  for (let line of lines) {
    line = line.trim();
    if (!line) continue;

    // Check if line is a section header
    const sectionMatch = line.match(/^([A-Za-z\s]+)(?:\s*\(\d+\))?$/);
    if (sectionMatch && !/^\d+/.test(line)) {
      const headerName = sectionMatch[1].trim();
      const recognized = ['Leader', 'Character', 'Event', 'Stage', 'Main', 'Side', 'Extra', 'Eggs', 'Pokémon', 'Trainer', 'Energy', 'Deck', 'Sideboard', 'Tamer'];
      if (recognized.some(s => s.toLowerCase() === headerName.toLowerCase())) {
        currentSection = headerName;
        continue;
      }
    }

    // Match card line like "4 Kumacy (OP14-102)" or "1 Nami (OP11-041)" or "4 Kumacy"
    const cardMatch = line.match(/^(\d+)\s+(.+?)(?:\s*\(([^)]+)\))?$/);
    if (cardMatch) {
      const qty = parseInt(cardMatch[1], 10);
      const name = cardMatch[2].trim();
      const code = cardMatch[3] ? cardMatch[3].trim() : undefined;
      results.push({
        name,
        quantity: qty,
        code,
        section: currentSection
      });
    }
  }

  return results;
}
